<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants';

import Icon from 'next/icon/Icon.vue';

const { contentAttributes, messageType, createdAt, attachments } =
  useMessageContext();
const { t } = useI18n();

const isIncomingCall = computed(
  () => messageType.value === MESSAGE_TYPES.INCOMING_CALL
);

const callIcon = computed(() =>
  isIncomingCall.value ? 'i-lucide-phone-incoming' : 'i-lucide-phone-outgoing'
);

const callLabel = computed(() =>
  isIncomingCall.value
    ? t('CONVERSATION.CALL.INCOMING')
    : t('CONVERSATION.CALL.OUTGOING')
);

const dispositionLabelMap = computed(() => ({
  ANSWERED: t('CONVERSATION.CALL.ANSWERED'),
  'NO ANSWER': t('CONVERSATION.CALL.NO_ANSWER'),
  BUSY: t('CONVERSATION.CALL.BUSY'),
  FAILED: t('CONVERSATION.CALL.FAILED'),
  CANCELED: t('CONVERSATION.CALL.CANCELED'),
  TRANSFERRED: t('CONVERSATION.CALL.TRANSFERRED') || 'Transferida',
}));

const labelForDisposition = disposition =>
  dispositionLabelMap.value[disposition] || disposition || '';

const formatSeconds = seconds => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const disposition = computed(
  () => contentAttributes.value?.disposition || 'ANSWERED'
);
const dispositionLabel = computed(() => labelForDisposition(disposition.value));
const isAnswered = computed(() => disposition.value === 'ANSWERED');
const duration = computed(() =>
  formatSeconds(contentAttributes.value?.billableSeconds)
);

const readableTime = computed(() => {
  const callDate = contentAttributes.value?.callDate;
  if (callDate)
    return messageTimestamp(
      new Date(callDate).getTime() / 1000,
      'LLL d, h:mm a'
    );
  return messageTimestamp(createdAt.value, 'LLL d, h:mm a');
});

const audioAttachment = computed(() =>
  attachments.value?.find(a => a.fileType === 'audio')
);

// Legs do timeline (whatsapp_calling com transferências)
const legs = computed(() => {
  const list = contentAttributes.value?.legs || [];
  return Array.isArray(list) ? list : [];
});
const isTimeline = computed(() => legs.value.length > 1);

// Single-call fallback (sem timeline) — usa attachment de áudio ou recording_url
const singleRecordingUrl = computed(() => {
  if (audioAttachment.value?.dataUrl) return audioAttachment.value.dataUrl;
  return contentAttributes.value?.recordingUrl;
});
const hasSingleRecording = computed(() => !!singleRecordingUrl.value);

// Agent info para o single-call layout
const agentName = computed(() => contentAttributes.value?.agentName || null);
const agentExtension = computed(
  () => contentAttributes.value?.agentExtension || null
);
const agentAvatar = computed(
  () => contentAttributes.value?.agentAvatar || null
);
const hasAgentInfo = computed(() => agentName.value || agentExtension.value);
const agentInitial = computed(() =>
  agentName.value ? agentName.value.charAt(0).toUpperCase() : '?'
);

const bubbleClass = computed(() =>
  isAnswered.value
    ? 'bg-n-solid-3 border border-n-weak'
    : 'bg-n-ruby-3 border border-n-ruby-6'
);
const iconClass = computed(() => {
  if (isAnswered.value)
    return isIncomingCall.value ? 'text-n-teal-11' : 'text-n-blue-11';
  return 'text-n-ruby-11';
});

// Helpers do timeline
const legAgentInitial = leg =>
  leg.agentName ? leg.agentName.charAt(0).toUpperCase() : '?';
const legDuration = leg => formatSeconds(leg.billableSeconds || leg.talkTime);
const legRing = leg => formatSeconds(leg.ringTime);
const legDispositionClass = leg => {
  if (leg.disposition === 'ANSWERED') return 'text-n-teal-11';
  if (leg.disposition === 'TRANSFERRED') return 'text-n-amber-11';
  return 'text-n-ruby-11';
};
const legIconBg = leg => {
  if (leg.disposition === 'TRANSFERRED') return 'bg-n-amber-3';
  if (leg.disposition === 'ANSWERED') return 'bg-n-teal-3';
  return 'bg-n-ruby-3';
};
const legIcon = leg => {
  if (leg.disposition === 'TRANSFERRED') return 'i-lucide-phone-forwarded';
  if (leg.disposition === 'ANSWERED') return 'i-lucide-phone-call';
  return 'i-lucide-phone-missed';
};
const totalDuration = computed(() =>
  formatSeconds(
    contentAttributes.value?.billableSeconds ||
      legs.value.reduce((s, l) => s + (l.billableSeconds || 0), 0)
  )
);
</script>

<template>
  <div
    v-if="isTimeline"
    v-tooltip.top="readableTime"
    class="call-timeline rounded-xl px-4 py-3 min-w-[260px] max-w-[360px] bg-n-solid-3 border border-n-weak"
    data-bubble-name="call-timeline"
  >
    <!-- Header -->
    <div class="flex items-center gap-3 pb-3 border-b border-n-weak">
      <div
        class="w-10 h-10 rounded-full flex items-center justify-center bg-n-alpha-2"
      >
        <Icon :icon="callIcon" :class="iconClass" class="size-5" />
      </div>
      <div class="flex-1 min-w-0">
        <span class="font-medium text-n-slate-12">{{ callLabel }}</span>
        <div class="flex items-center gap-1.5 mt-0.5 text-xs text-n-slate-11">
          <span>{{ legs.length }}
            {{ legs.length === 1 ? 'perna' : 'pernas' }}</span>
          <template v-if="totalDuration">
            <span>&bull;</span>
            <span>{{ totalDuration }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Legs -->
    <ol class="mt-3 space-y-3">
      <li v-for="(leg, idx) in legs" :key="leg.id || idx" class="relative pl-6">
        <span
          class="absolute left-1.5 top-1 w-3 h-3 rounded-full ring-2 ring-n-solid-3"
          :class="legIconBg(leg)"
        />
        <span
          v-if="idx < legs.length - 1"
          class="absolute left-[10px] top-4 bottom-[-12px] w-px bg-n-weak"
        />

        <div class="flex items-start gap-2">
          <img
            v-if="leg.agentAvatar"
            :src="leg.agentAvatar"
            :alt="leg.agentName"
            class="w-7 h-7 rounded-full object-cover"
          />
          <div
            v-else
            class="w-7 h-7 rounded-full bg-n-slate-6 flex items-center justify-center"
          >
            <span class="text-[10px] font-semibold text-n-slate-11">{{
              legAgentInitial(leg)
            }}</span>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1.5 flex-wrap">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ leg.agentName || `Ramal ${leg.extensionNumber || '?'}` }}
              </span>
              <span
                v-if="leg.extensionNumber"
                class="text-[11px] text-n-slate-10"
              >
                ({{ leg.extensionNumber }})
              </span>
            </div>

            <div class="flex items-center gap-1.5 text-[11px] mt-0.5">
              <Icon
                :icon="legIcon(leg)"
                :class="legDispositionClass(leg)"
                class="size-3.5"
              />
              <span :class="legDispositionClass(leg)">
                {{ labelForDisposition(leg.disposition) }}
              </span>
              <template v-if="legRing(leg)">
                <span class="text-n-slate-10">&bull; ring {{ legRing(leg) }}</span>
              </template>
              <template v-if="legDuration(leg)">
                <span class="text-n-slate-10">&bull; {{ legDuration(leg) }}</span>
              </template>
            </div>

            <div
              v-if="leg.transferredTo"
              class="text-[11px] text-n-amber-11 mt-0.5"
            >
              → Transferido para ramal {{ leg.transferredTo }}
            </div>

            <audio
              v-if="leg.recordingUrl"
              :src="leg.recordingUrl"
              controls
              preload="metadata"
              class="w-full h-8 mt-2"
            />
          </div>
        </div>
      </li>
    </ol>
  </div>

  <!-- Single-leg layout (Asterisk CDR convencional ou WhatsApp sem transferência) -->
  <div
    v-else
    v-tooltip.top="readableTime"
    :class="bubbleClass"
    class="call-bubble rounded-xl px-4 py-3 min-w-[200px] max-w-[300px]"
    data-bubble-name="call"
  >
    <div class="flex items-center gap-3">
      <div
        class="call-icon w-10 h-10 rounded-full flex items-center justify-center"
        :class="isAnswered ? 'bg-n-alpha-2' : 'bg-n-ruby-4'"
      >
        <Icon :icon="callIcon" :class="iconClass" class="size-5" />
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="font-medium text-n-slate-12">{{ callLabel }}</span>
        </div>
        <div class="flex items-center gap-1.5 mt-0.5">
          <span
            class="text-xs"
            :class="isAnswered ? 'text-n-slate-11' : 'text-n-ruby-11'"
          >
            {{ dispositionLabel }}
          </span>
          <template v-if="duration && isAnswered">
            <span class="text-xs text-n-slate-10">&bull;</span>
            <span class="text-xs text-n-slate-10">{{ duration }}</span>
          </template>
        </div>
      </div>
    </div>

    <div v-if="hasAgentInfo" class="mt-3 pt-3 border-t border-n-weak">
      <div class="flex items-center gap-2">
        <img
          v-if="agentAvatar"
          :src="agentAvatar"
          :alt="agentName"
          class="w-6 h-6 rounded-full object-cover"
        />
        <div
          v-else
          class="w-6 h-6 rounded-full bg-n-slate-6 flex items-center justify-center"
        >
          <span class="text-[10px] font-semibold text-n-slate-11">{{
            agentInitial
          }}</span>
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-xs text-n-slate-11 truncate">{{ agentName }}</span>
          <span v-if="agentExtension" class="text-xs text-n-slate-10 ml-1">
            ({{ agentExtension }})
          </span>
        </div>
      </div>
    </div>

    <div v-if="hasSingleRecording" class="mt-3 pt-3 border-t border-n-weak">
      <audio
        :src="singleRecordingUrl"
        controls
        preload="metadata"
        class="w-full h-8"
      />
      <div
        v-if="audioAttachment?.transcribedText"
        class="mt-2 p-2 text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg break-words"
      >
        {{ audioAttachment.transcribedText }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.call-bubble audio,
.call-timeline audio {
  height: 32px;
}

.call-bubble audio::-webkit-media-controls-panel,
.call-timeline audio::-webkit-media-controls-panel {
  background-color: transparent;
}
</style>
